import React, { useState } from "react";
import { PrimeIcons } from "primereact/api";
import { Button } from "primereact/button";
import { BaseApiServiceImpl } from "../../app_utils/api/BaseApiServiceImpl";
import { UserSessionUtils } from "../../app_utils/utils/UserSessionUtils";

interface GatewayAlert {
  id: number;
  gatewayType: string;
  gatewayName: string;
  providerName: string;
  alertType: string;
  message: string;
  acknowledged: boolean;
  dateCreated: string;
}

interface Props {
  alerts: GatewayAlert[];
  onAlertClick?: () => void;
  onAcknowledge?: (id: number) => void;
}

const GatewayHealthBanner = ({ alerts, onAlertClick, onAcknowledge }: Props) => {
  const [acknowledging, setAcknowledging] = useState<number | null>(null);

  if (!alerts || alerts.length === 0) return null;

  const hasCritical = alerts.some((a) => a.alertType === "CRITICAL");
  const bgColor = hasCritical
    ? "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)"
    : "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)";

  const alertSummary = alerts
    .slice(0, 3)
    .map((a) => a.gatewayName)
    .join(", ");
  const extraCount = alerts.length > 3 ? ` +${alerts.length - 3} more` : "";

  const handleAcknowledge = async (e: React.MouseEvent, alertId: number) => {
    e.stopPropagation();
    setAcknowledging(alertId);
    try {
      await new BaseApiServiceImpl(`/api/v1/gateway-health/alerts/${alertId}/acknowledge`)
        .postRequestWithJsonResponse({});
      onAcknowledge?.(alertId);
    } catch {
      // error handled by BaseApiServiceImpl
    } finally {
      setAcknowledging(null);
    }
  };

  return (
    <div
      className="card mb-3 cursor-pointer"
      style={{ background: bgColor, color: "white" }}
      onClick={onAlertClick}
    >
      <div className="flex align-items-center justify-content-between mb-3">
        <div className="flex align-items-center">
          <i
            className={hasCritical ? PrimeIcons.EXCLAMATION_TRIANGLE : PrimeIcons.INFO_CIRCLE}
            style={{ fontSize: "1.5rem", marginRight: "0.75rem" }}
          />
          <div>
            <span style={{ fontWeight: 600 }}>
              {alerts.length} gateway{alerts.length !== 1 ? "s" : ""} need
              attention
            </span>
            <div style={{ opacity: 0.85, fontSize: "0.875rem", marginTop: "0.25rem" }}>
              {alertSummary}
              {extraCount}
            </div>
          </div>
        </div>
        <span
          style={{
            background: "rgba(255,255,255,0.2)",
            borderRadius: "9999px",
            padding: "0.25rem 0.75rem",
            fontSize: "0.8rem",
          }}
        >
          {alerts.length} alert{alerts.length !== 1 ? "s" : ""}
        </span>
      </div>

      <div className="grid">
        {alerts.map((alert) => (
          <div
            key={alert.id}
            className="col-12"
            style={{
              background: "rgba(255,255,255,0.1)",
              borderRadius: "0.5rem",
              padding: "0.75rem",
              marginBottom: "0.5rem",
            }}
          >
            <div className="flex align-items-center justify-content-between">
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: "0.9rem" }}>
                  {alert.gatewayName}
                </div>
                <div style={{ opacity: 0.9, fontSize: "0.8rem" }}>
                  {alert.message}
                </div>
                <div style={{ opacity: 0.7, fontSize: "0.75rem", marginTop: "0.25rem" }}>
                  {new Date(alert.dateCreated).toLocaleString()}
                </div>
              </div>
              {!alert.acknowledged && UserSessionUtils.isSuperAdmin() && (
                <Button
                  icon={PrimeIcons.CHECK}
                  label="Acknowledge"
                  className="p-button-sm"
                  style={{ marginLeft: "1rem", background: "rgba(255,255,255,0.2)", border: "none", color: "white" }}
                  loading={acknowledging === alert.id}
                  onClick={(e) => handleAcknowledge(e, alert.id)}
                />
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default GatewayHealthBanner;
